import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../utils/api';
import { Wallet, Send, Receipt, QrCode, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownLeft, Plus, FileText, CreditCard, Trash2, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const Dashboard = () => {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFundDialog, setShowFundDialog] = useState(false);
  const [fundAmount, setFundAmount] = useState('');
  const [fundLoading, setFundLoading] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [chargingCard, setChargingCard] = useState(false);

  const fetchWallet = useCallback(async () => {
    try {
      const response = await api.get('/wallet');
      setWallet(response.data);
    } catch (error) {
      toast.error('Failed to fetch wallet data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSavedCards = useCallback(async () => {
    try {
      const res = await api.get('/saved-cards');
      setSavedCards(res.data.cards || []);
    } catch (error) {
      toast.error('Failed to load saved cards');
    }
  }, []);

  useEffect(() => {
    fetchWallet();
    fetchSavedCards();
    const interval = setInterval(fetchWallet, 30000);
    return () => clearInterval(interval);
  }, [fetchWallet, fetchSavedCards]);

  const handleFundWallet = async () => {
    if (!fundAmount || parseFloat(fundAmount) < 1) {
      toast.error(`Minimum funding amount is ${sym}1`);
      return;
    }

    setFundLoading(true);
    try {
      const response = await api.post('/payments/initialize', {
        amount: parseFloat(fundAmount)
      });

      const { access_code, reference, paystack_public_key } = response.data;

      setShowFundDialog(false);
      setFundAmount('');

      const popup = new window.PaystackPop();
      popup.resumeTransaction(access_code, {
        onSuccess: async () => {
          toast.success('Payment successful! Verifying...');
          try {
            await api.get(`/payments/verify/${reference}`);
            toast.success('Wallet funded successfully!');
          } catch {
            toast.error('Payment received. Wallet will update shortly.');
          }
          fetchWallet();
          fetchSavedCards();
        },
        onCancel: () => {
          toast.info('Payment cancelled');
        },
        onError: () => {
          toast.error('Payment failed. Please try again.');
        }
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initialize payment');
    } finally {
      setFundLoading(false);
    }
  };

  const handleChargeSavedCard = async () => {
    if (!selectedCard || !fundAmount || parseFloat(fundAmount) < 1) {
      toast.error(`Enter an amount (min ${sym}1) and select a card`);
      return;
    }
    setChargingCard(true);
    try {
      const res = await api.post(
        `/payments/charge-saved-card?amount=${parseFloat(fundAmount)}&authorization_code=${encodeURIComponent(selectedCard)}`
      );
      if (res.data.success) {
        toast.success(res.data.message || 'Card charged successfully!');
        setShowFundDialog(false);
        setFundAmount('');
        setSelectedCard(null);
        setTimeout(fetchWallet, 2000);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Charge failed');
    }
    setChargingCard(false);
  };

  const handleRemoveCard = async (authCode) => {
    try {
      await api.delete(`/saved-cards/${encodeURIComponent(authCode)}`);
      toast.success('Card removed');
      setSavedCards(prev => prev.filter(c => c.authorization_code !== authCode));
      if (selectedCard === authCode) setSelectedCard(null);
    } catch {
      toast.error('Failed to remove card');
    }
  };

  const sym = wallet?.currency_symbol || '$';
  const cur = wallet?.currency || 'USD';

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{fontFamily: 'DM Sans'}} data-testid="dashboard-heading">Dashboard</h1>
          <p className="text-muted-foreground" data-testid="dashboard-description">Manage your payments and transactions</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="md:col-span-2 border shadow-sm" data-testid="wallet-balance-card">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">Wallet Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-4xl font-bold balance-display" data-testid="wallet-balance">
                    <span className="currency-symbol">{sym}</span>{wallet?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">{cur}</p>
                </div>
                <Button 
                  onClick={() => setShowFundDialog(true)} 
                  size="sm" 
                  className="rounded-full"
                  data-testid="fund-wallet-button"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Fund Wallet
                </Button>
              </div>
            </CardContent>
          </Card>

          <Link to="/send" className="block" data-testid="quick-action-send">
            <Card className="border shadow-sm hover:border-primary/20 transition-colors h-full cursor-pointer">
              <CardContent className="pt-6">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Send className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-1">Send Money</h3>
                <p className="text-sm text-muted-foreground">Transfer to users</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/invoice" className="block" data-testid="quick-action-invoice">
            <Card className="border shadow-sm hover:border-primary/20 transition-colors h-full cursor-pointer">
              <CardContent className="pt-6">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Receipt className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-1">Create Invoice</h3>
                <p className="text-sm text-muted-foreground">Request payment</p>
              </CardContent>
            </Card>
          </Link>
        </div>

        <Card className="border shadow-sm" data-testid="recent-transactions-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle data-testid="recent-transactions-title">Recent Transactions</CardTitle>
              <Link to="/transactions">
                <Button variant="ghost" size="sm" className="rounded-full" data-testid="view-all-link">
                  View All
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {!wallet?.transactions || wallet.transactions.length === 0 ? (
              <div className="text-center py-12" data-testid="no-transactions">
                <Wallet className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No transactions yet</p>
                <p className="text-sm text-muted-foreground mt-2">Start by funding your wallet or sending money</p>
              </div>
            ) : (
              <div className="space-y-4">
                {wallet.transactions.map((transaction, index) => (
                  <div 
                    key={transaction.reference || index} 
                    className="flex items-center justify-between p-4 border rounded-lg transaction-card"
                    data-testid={`transaction-item-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                        transaction.amount > 0 ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {transaction.amount > 0 ? (
                          <ArrowDownLeft className="h-5 w-5 text-green-600" />
                        ) : (
                          <ArrowUpRight className="h-5 w-5 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium" data-testid={`transaction-description-${index}`}>{transaction.description}</p>
                        <p className="text-sm text-muted-foreground" data-testid={`transaction-date-${index}`}>
                          {new Date(transaction.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold ${
                        transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                      }`} data-testid={`transaction-amount-${index}`}>
                        {transaction.amount > 0 ? '+' : ''}
                        {sym}{Math.abs(transaction.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </p>
                      <button
                        className="text-xs text-primary hover:underline mt-1 inline-flex items-center gap-1"
                        onClick={() => navigate(`/receipt/${transaction.reference}`)}
                        data-testid={`dashboard-receipt-button-${index}`}
                      >
                        <FileText className="h-3 w-3" /> Receipt
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showFundDialog} onOpenChange={(open) => { setShowFundDialog(open); if (!open) { setSelectedCard(null); setFundAmount(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="fund-wallet-dialog">
          <DialogHeader>
            <DialogTitle data-testid="dialog-title">Fund Your Wallet</DialogTitle>
            <DialogDescription data-testid="dialog-description">
              Enter the amount you want to add to your wallet. Minimum amount is {sym}1.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount ({sym})</Label>
              <Input
                id="amount"
                type="number"
                placeholder="1000"
                value={fundAmount}
                onChange={(e) => setFundAmount(e.target.value)}
                min="1"
                step="0.01"
                data-testid="fund-amount-input"
              />
            </div>

            {savedCards.length > 0 && (
              <div className="space-y-2" data-testid="saved-cards-section">
                <Label className="text-xs text-muted-foreground">Saved Cards</Label>
                <div className="space-y-2">
                  {savedCards.map((card) => (
                    <div
                      key={card.authorization_code}
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedCard === card.authorization_code
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-muted/50'
                      }`}
                      onClick={() => setSelectedCard(
                        selectedCard === card.authorization_code ? null : card.authorization_code
                      )}
                      data-testid={`saved-card-${card.last4}`}
                    >
                      <div className="flex items-center gap-3">
                        <CreditCard className="h-5 w-5 text-primary" />
                        <div>
                          <p className="text-sm font-medium">
                            {card.brand?.toUpperCase() || card.card_type?.toUpperCase()} **** {card.last4}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {card.bank} {card.exp_month && card.exp_year ? `- ${card.exp_month}/${card.exp_year}` : ''}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRemoveCard(card.authorization_code); }}
                        className="text-muted-foreground hover:text-red-500 transition-colors p-1"
                        data-testid={`remove-card-${card.last4}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedCard ? (
              <Button
                onClick={handleChargeSavedCard}
                disabled={chargingCard || !fundAmount}
                className="w-full rounded-full payment-button"
                data-testid="charge-saved-card-button"
              >
                {chargingCard ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Charging...</>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Pay with Saved Card
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleFundWallet}
                disabled={fundLoading || !fundAmount}
                className="w-full rounded-full payment-button"
                data-testid="proceed-payment-button"
              >
                {fundLoading ? 'Processing...' : 'Pay with New Card'}
              </Button>
            )}

            {savedCards.length > 0 && selectedCard && (
              <p className="text-xs text-center text-muted-foreground">
                or <button onClick={() => setSelectedCard(null)} className="text-primary hover:underline">use a new card instead</button>
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;